import { useState } from 'react';
import FieldInputRenderer from '../fields/FieldInputRenderer';
import { createField, deleteField } from '../../../api/fieldsApi';
import { getNextVpsHostingFaqIndex } from './vpsHostingFaqFieldGroups';

const VpsHostingFaqEditor = ({
  sectionId,
  fields,
  fieldValues,
  updateFieldValue,
  faqGroups,
  onFieldsReload,
  isSaving,
}) => {
  const [isMutating, setIsMutating] = useState(false);
  const [deletingIndex, setDeletingIndex] = useState(null);

  const handleAddFaq = async () => {
    const nextIndex = getNextVpsHostingFaqIndex(fields);
    const baseSortOrder = 10 + nextIndex * 2;

    setIsMutating(true);
    try {
      await createField(sectionId, {
        key: `faq-${nextIndex}-question`,
        label: `FAQ ${nextIndex} Question`,
        fieldType: 'text',
        value: '',
        isRequired: false,
        isPublished: true,
        sortOrder: baseSortOrder,
      });

      await createField(sectionId, {
        key: `faq-${nextIndex}-answer`,
        label: `FAQ ${nextIndex} Answer`,
        fieldType: 'textarea',
        value: '',
        isRequired: false,
        isPublished: true,
        sortOrder: baseSortOrder + 1,
      });

      await onFieldsReload();
    } finally {
      setIsMutating(false);
    }
  };

  const handleDeleteFaq = async (group) => {
    const questionField = group.fields.find((field) => field.key.endsWith('-question'));
    const question = questionField ? fieldValues[questionField.id] : '';
    const label = question || `FAQ ${group.index}`;

    if (!window.confirm(`Remove "${label}" from the FAQ section?`)) {
      return;
    }

    setDeletingIndex(group.index);
    try {
      await Promise.all(group.fields.map((field) => deleteField(field.id)));
      await onFieldsReload();
    } finally {
      setDeletingIndex(null);
    }
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between gap-3">
        <p className="text-sm text-[#6b635c]">
          Add, edit, or remove FAQ items. Remember to save after editing field values.
        </p>
        <button
          type="button"
          onClick={handleAddFaq}
          disabled={isSaving || isMutating}
          className="shrink-0 rounded-md border border-[#e4d9c8] bg-[#faf8f4] px-4 py-2 text-sm font-medium text-[#130705] hover:bg-white disabled:opacity-60"
        >
          {isMutating ? 'Adding…' : 'Add FAQ'}
        </button>
      </div>

      <div className="grid gap-4">
        {faqGroups.map((group) => (
          <article
            key={group.index}
            className="rounded-md border border-[#e4d9c8] bg-[#faf8f4] p-4"
          >
            <div className="mb-4 flex items-center justify-between gap-3 border-b border-[#e4d9c8] pb-3">
              <p className="text-[12px] font-semibold uppercase tracking-[0.12em] text-[#8d7045]">
                FAQ {group.index}
              </p>
              <button
                type="button"
                onClick={() => handleDeleteFaq(group)}
                disabled={isSaving || isMutating || deletingIndex === group.index}
                className="rounded-md border border-red-200 bg-red-50 px-3 py-1.5 text-[12px] font-medium !text-red-700 !shadow-none hover:!bg-red-100 hover:!shadow-none disabled:opacity-60"
              >
                {deletingIndex === group.index ? 'Removing…' : 'Remove'}
              </button>
            </div>

            <div className="grid gap-3">
              {group.fields.map((field) => (
                <div key={field.id}>
                  <label
                    htmlFor={`field-${field.id}`}
                    className="mb-1.5 block text-[12px] font-medium text-[#6b635c]"
                  >
                    {field.label}
                  </label>
                  <FieldInputRenderer
                    field={field}
                    value={fieldValues[field.id] || ''}
                    onChange={(value) => updateFieldValue(field.id, value)}
                  />
                </div>
              ))}
            </div>
          </article>
        ))}
      </div>
    </div>
  );
};

export default VpsHostingFaqEditor;
